import React from "react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "./ui/accordion";

const FAQS = [
  {
    q: "How is pricing structured? Are there hidden fees?",
    a: "Every quote on MeinHaus is itemized line by line — labour, materials, permits, and contingency — so you see exactly what you're paying for before any work begins. We don't add platform fees on top of your contractor's price. The number you approve is the number you pay.",
  },
  {
    q: "What does the Done-Right Guarantee actually cover?",
    a: "If a milestone fails to meet the agreed scope or local code, we step in: we'll cover rework with the same contractor, swap to another vetted pro at no extra cost, or refund unreleased funds — your choice. The guarantee covers workmanship for 12 months after final sign-off.",
  },
  {
    q: "How does the milestone payment system work?",
    a: "Funds you deposit are held safely with MeinHaus. As your contractor hits each milestone (rough-in, drywall, final, etc.), you review the work — in person or via photos in your dashboard. Approve, and the corresponding milestone payment releases. No approval, no release.",
  },
  {
    q: "Who handles permits and inspections?",
    a: "Your contractor pulls all required permits as part of the scope, and inspections are scheduled in your timeline. We surface every permit number and inspection result in your dashboard so nothing slips through the cracks.",
  },
  {
    q: "What happens if I want a change mid-project?",
    a: "Change orders happen — that's normal. In your dashboard you can request a change, your contractor responds with the cost and timeline impact, and once you approve, the new scope is added to your itemized contract. Everything stays transparent.",
  },
  {
    q: "Are MeinHaus contractors insured and licensed?",
    a: "Yes. Every contractor on MeinHaus is verified for active licensing (where required), $2M+ liability insurance, WSIB coverage, and a track record of at least 12 months of completed work in the relevant trade. We re-verify annually.",
  },
  {
    q: "Where do you operate?",
    a: "We're currently active across the Greater Toronto Area — Toronto, Mississauga, Oshawa, Brampton, Vaughan, Markham, Richmond Hill, Pickering, Ajax, Whitby, and surrounding regions. New cities rolling out in 2026.",
  },
  {
    q: "How long does a typical project take?",
    a: "It depends on scope: a paint refresh can be 3–5 days, a kitchen 4–8 weeks, a basement 6–12 weeks, a major addition 4–8 months. Every quote includes a fixed timeline with milestone dates so you always know what's next.",
  },
];

const FAQSection = () => {
  return (
    <section
      data-testid="faq-section"
      className="relative w-full bg-white pt-16 sm:pt-24 lg:pt-32 pb-16 sm:pb-24 lg:pb-32 border-t border-slate-200"
    >
      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left — sticky header */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="block h-px w-8 bg-[#1AA0DD]" />
                <span className="text-[11px] sm:text-[12px] font-bold tracking-[0.24em] uppercase text-[#1AA0DD]">
                  Questions, answered
                </span>
              </div>
              <h2
                className="font-extrabold text-[#0f172a] tracking-tight text-[28px] sm:text-[40px] lg:text-[48px] leading-[1.05]"
                style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
              >
                The <span style={{ color: "#F26B1F" }}>fine print</span>,
                without the fine print.
              </h2>
              <p className="mt-5 text-[#475569] text-[15px] sm:text-[16px] leading-[1.7] max-w-[460px]">
                The exact questions homeowners ask before saying yes. If
                yours isn't here, we'll answer it on a 10-minute call — no
                pitch, no pressure.
              </p>

              <div className="mt-8 hidden lg:flex items-center gap-3">
                <span
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full"
                  style={{ backgroundColor: "rgba(26,160,221,0.12)" }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 4 H13 V11 H8 L4 14 V11 H3 Z"
                      stroke="#1AA0DD"
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <a
                  href="#contact"
                  data-testid="faq-cta-talk"
                  className="text-[14px] font-semibold text-[#0f172a] hover:text-[#1AA0DD] transition-colors"
                >
                  Still have questions? Talk to a human →
                </a>
              </div>
            </div>
          </div>

          {/* Right — accordion */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-slate-200 first:border-t first:border-slate-200"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="py-6 lg:py-7 text-[16px] sm:text-[18px] lg:text-[19px] font-bold text-[#0f172a] hover:no-underline hover:text-[#1AA0DD] transition-colors text-left tracking-tight gap-4 [&[data-state=open]>svg]:text-[#F26B1F]"
                  >
                    <span className="flex items-start gap-4">
                      <span
                        className="text-[12px] font-bold tracking-[0.16em] text-[#1AA0DD] pt-1 tabular-nums"
                        aria-hidden="true"
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1">{item.q}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 lg:pb-7 pl-9 pr-4">
                    <p className="text-[#475569] text-[14.5px] leading-[1.7] max-w-[640px]">
                      {item.a}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Mobile-only bottom CTA */}
            <div className="mt-8 lg:hidden">
              <a
                href="#contact"
                data-testid="faq-cta-talk-mobile"
                className="text-[14px] font-semibold text-[#0f172a] hover:text-[#1AA0DD] transition-colors"
              >
                Still have questions? Talk to a human →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
